import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export type StatusTone = "success" | "warning" | "destructive" | "info" | "muted" | "outline";

const TONE_COLORS: Record<StatusTone, { bgcolor: string; color: string }> = {
  success: { bgcolor: "color-mix(in oklab, var(--success) 14%, transparent)", color: "var(--success)" },
  warning: { bgcolor: "color-mix(in oklab, var(--warning) 14%, transparent)", color: "var(--warning)" },
  destructive: { bgcolor: "color-mix(in oklab, var(--destructive) 14%, transparent)", color: "var(--destructive)" },
  info: { bgcolor: "color-mix(in oklab, var(--info) 14%, transparent)", color: "var(--info)" },
  muted: { bgcolor: "var(--muted)", color: "var(--muted-foreground)" },
  outline: { bgcolor: "transparent", color: "var(--body)" },
};

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Maps any backend status string to a label and a semantic tone. */
export const getStatusConfig = (status: string) => {
  const s = (status || "").toLowerCase().replace(/_/g, " ");
  let tone: StatusTone = "muted";
  let label = status ? capitalize(s) : "Unknown";

  switch (s) {
    case "active":
    case "approved":
    case "accepted":
    case "open":
    case "verified":
    case "paid":
    case "success":
    case "successful":
      tone = "success";
      break;
    case "pending":
    case "reviewed":
    case "under review":
    case "submitted":
    case "processing":
    case "in review":
      tone = "warning";
      if (s === "reviewed" || s === "under review") label = "Pending";
      break;
    case "interview":
    case "interview scheduled":
    case "in progress":
    case "scheduled":
      tone = "info";
      if (s.startsWith("interview")) label = "Interview";
      break;
    case "rejected":
    case "closed":
    case "failed":
    case "cancelled":
    case "canceled":
    case "suspended":
      tone = "destructive";
      break;
    case "completed":
    case "filled":
    case "draft":
      tone = "outline";
      break;
  }

  return { label, tone, ...TONE_COLORS[tone] };
};

export default function StatusChip({
  status,
  className,
}: {
  status: string;
  className?: string;
  /** @deprecated MUI styling hook; ignored. */
  sx?: unknown;
}) {
  const config = getStatusConfig(status);
  return (
    <Badge variant={config.tone} dot className={cn("capitalize", className)}>
      {config.label}
    </Badge>
  );
}
